import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Trash2, Megaphone, Mail, Loader2 } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useI18n } from "../../i18n";
import { api, formatError } from "../../lib/api";

export const MessagesTab = () => {
  const { t } = useI18n();
  const [members, setMembers] = useState([]);
  const [sent, setSent] = useState([]);
  const [f, setF] = useState({ recipient: "all", title: "", body: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/admin/messages").then((r) => setSent(r.data)), []);
  useEffect(() => { load(); api.get("/admin/users").then((r) => setMembers(r.data)); }, [load]);

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/messages", { recipient_id: f.recipient === "all" ? null : f.recipient, title: f.title, body: f.body });
      toast.success(t("adm_msg_sent")); setF({ ...f, title: "", body: "" }); load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };
  const remove = async (id) => { await api.delete(`/admin/messages/${id}`); load(); };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_1.3fr]">
      <form onSubmit={send} className="lux-card h-fit space-y-4 p-6" data-testid="admin-message-form">
        <h3 className="font-serif text-xl font-semibold">{t("adm_msg_send_title")}</h3>
        <div className="space-y-1.5"><Label>{t("adm_msg_recipient")}</Label>
          <Select value={f.recipient} onValueChange={(v) => setF({ ...f, recipient: v })}>
            <SelectTrigger data-testid="admin-msg-recipient-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="admin-msg-recipient-all">{t("adm_msg_all")}</SelectItem>
              {members.map((m) => <SelectItem key={m.id} value={m.id} data-testid={`admin-msg-recipient-${m.id}`}>{m.full_name} · {m.phone}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label>{t("adm_msg_title")}</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required maxLength={150} data-testid="admin-msg-title-input" /></div>
        <div className="space-y-1.5"><Label>{t("adm_msg_body")}</Label><Textarea rows={6} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} required data-testid="admin-msg-body-input" /></div>
        <button type="submit" disabled={busy} className="btn-gold w-full" data-testid="admin-msg-send-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}{t("adm_msg_send")}</button>
      </form>
      <div className="lux-card p-6">
        <h3 className="font-serif text-xl font-semibold">{t("adm_msg_history")}</h3>
        {sent.length === 0 ? <p className="mt-6 text-sm text-muted-foreground" data-testid="admin-msg-none">{t("adm_msg_none")}</p> : (
          <ul className="mt-4 divide-y">
            {sent.map((m) => (
              <li key={m.id} className="flex gap-3 py-4" data-testid={`admin-msg-item-${m.id}`}>
                {m.recipient_id ? <Mail className="mt-1 h-4 w-4 shrink-0" /> : <Megaphone className="mt-1 h-4 w-4 shrink-0" style={{ color: "var(--gold)" }} />}
                <div className="min-w-0 flex-1">
                  <p className="font-semibold">{m.title}</p>
                  <p className="text-xs text-muted-foreground">{m.recipient_id ? m.recipient_name || "—" : t("adm_msg_all")} · {new Date(m.created_at).toLocaleString()} · {m.read_count} {t("adm_msg_read")}</p>
                  <p className="mt-1 line-clamp-2 whitespace-pre-line text-sm text-muted-foreground">{m.body}</p>
                </div>
                <button onClick={() => remove(m.id)} title={t("adm_msg_delete")} className="h-8 w-8 shrink-0 rounded-full border text-red-500 hover:bg-accent" data-testid={`admin-msg-delete-${m.id}`}><Trash2 className="mx-auto h-4 w-4" /></button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
